// src/pages/ProductDetail/ProductDetailPage.js
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Star,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Calendar,
  Users,
  MessageCircle,
  Radar,
  Target,
  Gauge,
  BarChart2,
  Heart,
  Sparkles,
  Droplet,
  ImageOff
} from 'lucide-react';
import Chart from 'react-apexcharts';
import { metricsAPI } from '../../api';
import { LoadingSkeleton } from '../../components/common/UI';
import { useLanguage } from '../../contexts/LanguageContext';

// ============================================
// HELPER FUNCTIONS
// ============================================

// Calculate Vitality Score from available metrics
const calculateVitalityScore = (basicInfo, reviewSummary) => {
  let score = 0;

  // Review volume score (max 30 pts)
  const reviewScore = Math.min((basicInfo.total_reviews || 0) / 10, 30);
  score += reviewScore;

  // Review density score (max 40 pts)
  const densityScore = Math.min((basicInfo.avg_review_density || 0) * 100, 40);
  score += densityScore;

  // Trend score based on summary directions (max 30 pts)
  if (reviewSummary) {
    Object.values(reviewSummary).forEach(item => {
      if (item.direction === '상승' || item.direction === 'Increasing') {
        score += 10;
      } else if (item.direction === '변화 없음' || item.direction === 'No Change' || item.direction === 'Stable') {
        score += 5;
      }
    });
  }

  return Math.min(score, 100).toFixed(1);
};

// Calculate Market Age (days) from review data
const calculateMarketAge = (basicInfo) => {
  if (basicInfo.avg_review_density && basicInfo.avg_review_density > 0) {
    return Math.round(basicInfo.total_reviews / basicInfo.avg_review_density);
  }
  return null;
};

// Map popularity category to numeric score
const getPopularityScore = (category) => {
  const map = {
    'Very High': 90, '매우 높음': 90,
    'High': 75, '높음': 75,
    'Medium': 51, '보통': 51,
    'Low': 25, '낮음': 25,
    'Very Low': 10, '매우 낮음': 10
  };
  return map[category] || 50;
};

// Get trend direction info
const getTrendInfo = (direction) => {
  if (direction === '상승' || direction === 'Increasing') {
    return { icon: ArrowUpRight, color: 'text-emerald-500', label: 'Increasing' };
  } else if (direction === '하락' || direction === 'Decreasing') {
    return { icon: ArrowDownRight, color: 'text-rose-500', label: 'Decreasing' };
  }
  return { icon: null, color: 'text-slate-500', label: 'No Change' };
};

// ============================================
// SUB-COMPONENTS
// ============================================

// Qualitative Insights List Component
const InsightsList = ({ data, maxItems = 5 }) => {
  if (!data || data.length === 0) return null;

  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, maxItems);
  const maxVal = sorted[0]?.value || 1;

  return (
    <div className="flex flex-col gap-4">
      {sorted.map((item, index) => {
        const percentage = Math.round((item.value / maxVal) * 100);
        const isTop = index === 0;

        return (
          <div key={index} className="group cursor-default">
            <div className="flex justify-between items-end mb-1">
              <span className={`${isTop ? 'text-base font-bold text-slate-900' : 'text-sm font-medium text-slate-500 group-hover:text-slate-700'} transition-colors`}>
                {item.label}
              </span>
              <span className={`font-mono text-xs ${isTop ? 'text-slate-900 font-bold' : 'text-slate-400'}`}>
                {item.value}
              </span>
            </div>
            <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${isTop ? 'bg-slate-800' : 'bg-slate-300 group-hover:bg-slate-400'} transition-all duration-500 ease-out`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const ProductDetailPage = () => {
  const { translate, language } = useLanguage();
  const { productId } = useParams();
  const [productData, setProductData] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch product data
  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiLanguage = language === 'en' ? 'en' : 'ko';
        const response = await metricsAPI.getProductMetrics(productId, apiLanguage);

        if (response && response.data) {
          setProductData(response.data);
        } else {
          setError('Unexpected response structure');
        }
      } catch (error) {
        console.error('Failed to fetch product details:', error);
        setError('Failed to fetch product details');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductDetail();
    }
  }, [productId, language]);

  // Memoized calculations
  const calculatedData = useMemo(() => {
    if (!productData) return null;

    const { basic_info, review_info } = productData;

    return {
      vitalityScore: calculateVitalityScore(basic_info, review_info?.summary),
      marketAge: calculateMarketAge(basic_info),
      popularityScore: getPopularityScore(basic_info?.popularity_category),
    };
  }, [productData]);

  // Chart configurations
  const chartConfigs = useMemo(() => {
    if (!productData?.review_info) return null;

    const { review_info } = productData;
    const aspectScores = review_info.aspect_scores || {};

    // Radar Chart - Product Spectrum
    const radarOptions = {
      series: [{
        name: 'Score',
        data: [
          aspectScores.moisture || 0,
          aspectScores.absorption || 0,
          aspectScores.texture || 0,
          aspectScores.effectiveness || 0
        ]
      }],
      options: {
        chart: {
          type: 'radar',
          height: 280,
          toolbar: { show: false },
          fontFamily: 'DM Sans, sans-serif',
          dropShadow: { enabled: true, blur: 4, left: 0, top: 4, opacity: 0.1 }
        },
        labels: [
          translate('productDetailPage.performance.moisture'),
          translate('productDetailPage.performance.absorption'),
          translate('productDetailPage.performance.texture'),
          translate('productDetailPage.performance.effectiveness')
        ],
        stroke: { width: 2, colors: ['#E11D48'] },
        fill: { opacity: 0.2, colors: ['#FB7185'] },
        markers: {
          size: 5,
          colors: ['#fff'],
          strokeColors: '#E11D48',
          strokeWidth: 2,
          hover: { size: 7 }
        },
        yaxis: { show: false, min: 0, max: 100 },
        xaxis: {
          labels: {
            style: {
              colors: ['#64748B', '#64748B', '#64748B', '#64748B'],
              fontSize: '11px',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600
            }
          }
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: '#F1F5F9',
              connectorColors: '#F1F5F9'
            }
          }
        },
        tooltip: {
          theme: 'light',
          y: { formatter: (val) => `${val} pts` }
        }
      }
    };

    // RadialBar Chart - Voice of Customer (Effects)
    const effectsData = review_info.keywords?.effects?.labels?.map((label, i) => ({
      label,
      value: review_info.keywords.effects.values[i]
    })).slice(0, 3) || [];

    const maxEffect = Math.max(...effectsData.map(d => d.value), 1);
    const radialOptions = {
      series: effectsData.map(d => Math.round((d.value / maxEffect) * 100)),
      options: {
        chart: { type: 'radialBar', height: 300, fontFamily: 'DM Sans, sans-serif' },
        labels: effectsData.map(d => d.label),
        colors: ['#10B981', '#34D399', '#A7F3D0'],
        plotOptions: {
          radialBar: {
            hollow: { size: '55%' },
            track: { show: true, background: '#F8FAFC', strokeWidth: '100%' },
            dataLabels: {
              name: { fontSize: '11px', color: '#64748B', offsetY: -5, fontWeight: 500 },
              value: {
                fontSize: '20px',
                fontWeight: 700,
                color: '#0F172A',
                offsetY: 5,
                formatter: (val) => `${val}%`
              },
              total: {
                show: true,
                label: translate('productDetailPage.charts.dominant') || 'Dominant',
                color: '#94A3B8',
                fontSize: '11px',
                fontWeight: 500,
                formatter: () => effectsData[0]?.label || '-'
              }
            }
          }
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '11px',
          fontWeight: 500,
          labels: { colors: '#64748B' },
          markers: { radius: 12 },
          itemMargin: { horizontal: 10, vertical: 0 }
        }
      }
    };

    // Semi-Donut Chart - Texture Profile
    const textureData = review_info.keywords?.texture?.labels?.map((label, i) => ({
      label,
      value: review_info.keywords.texture.values[i]
    })).slice(0, 4) || [];

    const gaugeOptions = {
      series: textureData.map(d => d.value),
      options: {
        chart: { type: 'donut', height: 300, fontFamily: 'DM Sans, sans-serif' },
        labels: textureData.map(d => d.label),
        colors: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD'],
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 90,
            offsetY: 10,
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: { show: true, fontSize: '11px', color: '#64748B', offsetY: -20, fontWeight: 500 },
                value: { show: true, fontSize: '24px', fontWeight: 700, color: '#0F172A', offsetY: -10 },
                total: {
                  show: true,
                  label: translate('productDetailPage.charts.totalMentions') || 'Total Mentions',
                  color: '#94A3B8',
                  fontSize: '11px',
                  fontWeight: 500,
                  formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                }
              }
            }
          }
        },
        grid: { padding: { bottom: -80 } },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { y: { formatter: (val) => `${val} mentions` } }
      }
    };

    // Bar Chart - Skin Concern Analysis
    const skinData = review_info.keywords?.skin_condition?.labels?.map((label, i) => ({
      label,
      value: review_info.keywords.skin_condition.values[i]
    })).slice(0, 5) || [];

    const lollipopOptions = {
      series: [{ name: 'Concerns', data: skinData.map(d => d.value) }],
      options: {
        chart: { type: 'bar', height: 280, toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
        plotOptions: {
          bar: { borderRadius: 10, horizontal: false, columnWidth: '6px', endingShape: 'rounded' }
        },
        colors: ['#F59E0B'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: skinData.map(d => d.label),
          labels: { style: { fontSize: '11px', colors: '#64748B', fontWeight: 500 } },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          show: true,
          labels: { style: { colors: '#94A3B8', fontSize: '11px' }, offsetX: -10 }
        },
        grid: {
          show: true,
          borderColor: '#F1F5F9',
          strokeDashArray: 3,
          padding: { top: 0, right: 0, bottom: 0, left: 10 }
        },
        tooltip: { theme: 'light', y: { formatter: (val) => `${val} mentions` } }
      }
    };

    return { radarOptions, radialOptions, gaugeOptions, lollipopOptions };
  }, [productData, translate]);

  // Loading state
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-8 mt-24">
        <LoadingSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-8 mt-24">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h1 className="font-serif text-2xl font-bold text-slate-900">Error</h1>
          <p className="text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!productData) {
    return (
      <div className="max-w-6xl mx-auto p-8 mt-24">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h1 className="font-serif text-2xl font-bold text-slate-900">No product data found</h1>
        </div>
      </div>
    );
  }

  const { basic_info, other_info, review_info } = productData;

  // Get other_info values with fallbacks
  const getOtherInfo = (koKey, enKey) => {
    return other_info?.[koKey] || other_info?.[enKey] || '-';
  };

  // Prepare qualitative insights data
  const satisfactionData = review_info?.keywords?.satisfaction?.labels?.map((label, i) => ({
    label,
    value: review_info.keywords.satisfaction.values[i]
  })) || [];

  const effectsListData = review_info?.keywords?.effects?.labels?.map((label, i) => ({
    label,
    value: review_info.keywords.effects.values[i]
  })) || [];

  const textureListData = review_info?.keywords?.texture?.labels?.map((label, i) => ({
    label,
    value: review_info.keywords.texture.values[i]
  })) || [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen p-4 md:p-8 font-sans">
      <div className="max-w-6xl mx-auto space-y-6 mt-16">

        {/* ============================================
            SECTION 1: Product Header
            ============================================ */}
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative overflow-hidden">
          <div className="flex flex-col lg:flex-row gap-8 items-start">

            {/* Product Image */}
            <div className="relative group shrink-0 mx-auto lg:mx-0">
              <div className="relative w-48 h-48 bg-white rounded-xl border border-slate-100 flex items-center justify-center overflow-hidden">
                {imageLoadError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50 text-slate-400">
                    <ImageOff size={24} className="mb-1 opacity-70" />
                  </div>
                ) : (
                  <img
                    src={basic_info.s3_key}
                    alt={basic_info.group_name}
                    className="w-full h-full object-cover transform transition-transform duration-700 ease-out group-hover:scale-105"
                    onError={() => setImageLoadError(true)}
                  />
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 w-full pt-1">
              {/* Brand & Lifecycle Badge */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                  {basic_info.brand_name}
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-200" />
                <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                  {basic_info.lifecycle_stage || 'Stable'}
                </span>
              </div>

              {/* Product Title */}
              <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight mb-5">
                {basic_info.group_name}
              </h1>

              {/* Key Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6 text-sm">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {translate('productDetailPage.otherInfoLabels.mainIngredients')}
                  </span>
                  <div className="font-medium text-slate-700 truncate">
                    {getOtherInfo('주요 성분', 'Main Ingredients')}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {translate('productDetailPage.otherInfoLabels.coreTechnology')}
                  </span>
                  <div className="font-medium text-slate-700">
                    {getOtherInfo('핵심 기술', 'Core Technology')}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {translate('productDetailPage.otherInfoLabels.productComposition')}
                  </span>
                  <div className="font-medium text-slate-700">
                    {getOtherInfo('제품 구성', 'Product Composition')}
                  </div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex flex-wrap gap-6 items-center pt-6 border-t border-slate-50">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-lg font-bold text-slate-900">
                    {basic_info.weighted_rating?.toFixed(1) || '-'}
                  </span>
                  <span className="text-xs text-slate-400">
                    ({basic_info.total_reviews?.toLocaleString() || 0} {translate('productDetailPage.metrics.reviews')})
                  </span>
                </div>
                {basic_info.avg_discount_rate > 0 && (
                  <div className="px-2 py-0.5 rounded text-rose-600 text-xs font-bold bg-rose-50 border border-rose-100">
                    {basic_info.avg_discount_rate}% OFF
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ============================================
            SECTION 2: Market Intelligence (Bento Grid)
            ============================================ */}
        <section>
          <div className="mb-4 px-1">
            <h2 className="font-serif text-xl font-bold text-slate-900">
              {translate('productDetailPage.sections.marketTrends')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* Vitality Score (Span 2) */}
            <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between h-64">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-rose-50 rounded-lg text-rose-500">
                    <Activity className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Vitality Score
                  </span>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                  <TrendingUp className="w-3 h-3" />
                  {calculatedData?.vitalityScore >= 70 ? 'Top 15%' : calculatedData?.vitalityScore >= 50 ? 'Top 30%' : 'Active'}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-5xl font-serif font-bold text-slate-900">
                  {calculatedData?.vitalityScore || '0'}
                </div>
                <p className="text-slate-400 text-xs mt-2">
                  Momentum based on recent consumer interaction
                </p>
              </div>

              <div className="mt-auto pt-6 flex gap-8 border-t border-slate-50">
                {review_info?.summary && Object.entries(review_info.summary).slice(0, 2).map(([key, value], idx) => {
                  const trendInfo = getTrendInfo(value.direction);
                  const TrendIcon = trendInfo.icon;
                  return (
                    <div key={idx}>
                      <div className="text-[10px] font-bold text-slate-400 uppercase mb-0.5">
                        {key.includes('반응') ? 'Reaction' : key.includes('만족') ? 'Satisfaction' : 'Engagement'}
                      </div>
                      <div className="flex items-center gap-1 text-sm font-bold text-slate-700">
                        {trendInfo.label}
                        {TrendIcon && <TrendIcon className={`w-3 h-3 ${trendInfo.color}`} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Market Age */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between h-64">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-slate-50 rounded-lg text-slate-500">
                  <Calendar className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Market Age
                </span>
              </div>

              <div>
                <div className="text-4xl font-serif font-bold text-slate-900">
                  {calculatedData?.marketAge?.toLocaleString() || 'N/A'}
                </div>
                <div className="text-slate-400 text-xs mt-1">Days since launch</div>
              </div>

              <div className="space-y-2 pt-4 border-t border-slate-50">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Participation</span>
                  <span className="font-medium text-slate-700">
                    {review_info?.summary?.['소비자 참여도']?.direction ||
                      review_info?.summary?.['Consumer Engagement']?.direction ||
                      'No Change'}
                  </span>
                </div>
              </div>
            </div>

            {/* Stacked Stats */}
            <div className="flex flex-col gap-4 h-64">
              {/* Popularity */}
              <div className="flex-1 bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-center">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {translate('productDetailPage.metrics.popularity')}
                  </span>
                  <div className="p-1 bg-indigo-50 text-indigo-500 rounded">
                    <Users className="w-3 h-3" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <div className="text-2xl font-bold text-slate-900">
                    {calculatedData?.popularityScore || 50}
                  </div>
                  <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                    {basic_info.popularity_category || 'Medium'}
                  </span>
                </div>
                <div className="w-full bg-slate-100 h-1 rounded-full mt-3">
                  <div
                    className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${calculatedData?.popularityScore || 50}%` }}
                  />
                </div>
              </div>

              {/* Daily Reviews */}
              <div className="flex-1 bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-center">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {translate('productDetailPage.metrics.dailyReviews')}
                  </span>
                  <div className="p-1 bg-blue-50 text-blue-500 rounded">
                    <MessageCircle className="w-3 h-3" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-slate-900">
                  {basic_info.avg_review_density?.toFixed(2) || '0.00'}
                </div>
                <div className="text-[10px] text-slate-400">Reviews per day</div>
              </div>
            </div>

          </div>
        </section>

        {/* ============================================
            SECTION 3: Consumer Analysis (Charts)
            ============================================ */}
        <section>
          <div className="mb-4 px-1">
            <h2 className="font-serif text-xl font-bold text-slate-900">
              Consumer Analysis
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Product Spectrum (Radar) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-purple-50 rounded-lg text-purple-500">
                  <Radar className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {translate('productDetailPage.charts.performanceIndicators')}
                </span>
              </div>
              <div className="flex-1 min-h-[250px] -ml-2">
                {chartConfigs?.radarOptions && (
                  <Chart
                    options={chartConfigs.radarOptions.options}
                    series={chartConfigs.radarOptions.series}
                    type="radar"
                    height={280}
                  />
                )}
              </div>
            </div>

            {/* Voice of Customer (RadialBar) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-emerald-50 rounded-lg text-emerald-500">
                  <Target className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {translate('productDetailPage.charts.effectKeywords')}
                </span>
              </div>
              <div className="flex-1 min-h-[250px]">
                {chartConfigs?.radialOptions && (
                  <Chart
                    options={chartConfigs.radialOptions.options}
                    series={chartConfigs.radialOptions.series}
                    type="radialBar"
                    height={300}
                  />
                )}
              </div>
            </div>

            {/* Texture Profile (Semi-Donut) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-sky-50 rounded-lg text-sky-500">
                  <Gauge className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {translate('productDetailPage.sections.textureAnalysis')}
                </span>
              </div>
              <div className="flex-1 min-h-[250px] flex items-center justify-center">
                {chartConfigs?.gaugeOptions && (
                  <Chart
                    options={chartConfigs.gaugeOptions.options}
                    series={chartConfigs.gaugeOptions.series}
                    type="donut"
                    height={300}
                  />
                )}
              </div>
              {/* Custom Legend */}
              <div className="flex items-center justify-center gap-6 -mt-8">
                {textureListData.slice(0, 4).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD'][idx] }}
                    />
                    <span className="text-xs font-medium text-slate-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Skin Concern Analysis (Bar/Lollipop) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-amber-50 rounded-lg text-amber-500">
                  <BarChart2 className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {translate('productDetailPage.charts.skinConcerns')}
                </span>
              </div>
              <div className="flex-1 min-h-[250px]">
                {chartConfigs?.lollipopOptions && (
                  <Chart
                    options={chartConfigs.lollipopOptions.options}
                    series={chartConfigs.lollipopOptions.series}
                    type="bar"
                    height={280}
                  />
                )}
              </div>
            </div>

          </div>
        </section>

        {/* ============================================
            SECTION 4: Qualitative Insights
            ============================================ */}
        <section className="mt-4">
          <div className="mb-4 px-1">
            <h2 className="font-serif text-xl font-bold text-slate-900">
              Qualitative Insights
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Sentiment */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-1.5 bg-slate-50 rounded text-slate-600">
                  <Heart className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-900 uppercase tracking-widest">
                  {translate('productDetailPage.charts.sentimentAnalysis')}
                </span>
              </div>
              <div className="flex-1">
                <InsightsList data={satisfactionData} />
              </div>
            </div>

            {/* Key Effects */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-1.5 bg-slate-50 rounded text-slate-600">
                  <Sparkles className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-900 uppercase tracking-widest">
                  Key Effects
                </span>
              </div>
              <div className="flex-1">
                <InsightsList data={effectsListData} />
              </div>
            </div>

            {/* Texture Profile */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-1.5 bg-slate-50 rounded text-slate-600">
                  <Droplet className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-slate-900 uppercase tracking-widest">
                  Texture Profile
                </span>
              </div>
              <div className="flex-1">
                <InsightsList data={textureListData} />
              </div>
            </div>

          </div>
        </section>

      </div>
    </div>
  );
};

export default ProductDetailPage;
